import { useState, useMemo, useCallback, useEffect } from 'react';
import fetch from '@api/fetchers';
import { useRouter } from 'next/router';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getUserOnboardingPreference } from '@api/selectors/auth';
import useContestOnboardingPreference from '@api/hooks/goal/test-series/useContestOnboardingPreference';
import { emptyList } from '@constants/empty';
import { COMBAT_TYPES } from '../../constants';

const COMBAT_TYPE_OPTIONS = [
  { label: 'All', value: COMBAT_TYPES.ALL },
  { label: 'Upcoming', value: COMBAT_TYPES.UPCOMING },
  { label: 'Past', value: COMBAT_TYPES.PAST }
];

const useCombatFilter = () => {
  const { query } = useRouter();
  const { goalUID } = query;

  const { accessControl, refetchAccessControlData } = useAccessControl({
    goalUID
  });
  const [userPreference, setUserPreference] = useState(
    accessControl ? getUserOnboardingPreference(accessControl) : null
  );
  const { contestOnboardingPreferenceData } =
    useContestOnboardingPreference(goalUID);
  const firstPreferences =
    contestOnboardingPreferenceData?.[0]?.options || emptyList;

  const preferenceOptions = useMemo(
    () =>
      firstPreferences.map((preference) => ({
        label: preference.description,
        value: preference.uid
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstPreferences]
  );

  const [selectedFirstLevel, setSelectedFirstLevel] = useState(
    COMBAT_TYPE_OPTIONS[1]
  );

  const [selectedSeceondLevel, setSelectedSeceondLevel] = useState(
    preferenceOptions[0]
  );

  useEffect(() => {
    if (accessControl) {
      setUserPreference(getUserOnboardingPreference(accessControl));
    }
  }, [accessControl]);

  useEffect(() => {
    const data = preferenceOptions.find(
      ({ value }) => value === userPreference?.uid
    );

    setSelectedSeceondLevel(data || preferenceOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreference?.uid]);

  const handleOnFirstLevelChange = useCallback((value) => {
    setSelectedFirstLevel(value);
  }, []);

  const handleOnSecondLevelChange = useCallback(async (data) => {
    setSelectedSeceondLevel(data);
    // Update user onboarding preference
    await fetch('v1/user/me/onboarding_preference/', {
      requestType: 'POST',
      body: {
        goal_uid: goalUID,
        option_uids: data.value
      }
    });
    refetchAccessControlData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showFirstLevel = !!COMBAT_TYPE_OPTIONS.length;
  const showSecondLevel = !!preferenceOptions.length;
  const key = `combat_${goalUID}_${selectedFirstLevel.value}_${selectedSeceondLevel?.value}`;

  return {
    key,
    showFirstLevel,
    selectedFirstLevel,
    firstLevelOptions: COMBAT_TYPE_OPTIONS,
    handleOnFirstLevelChange,
    showSecondLevel,
    selectedSeceondLevel,
    secondLevelOptions: preferenceOptions,
    handleOnSecondLevelChange
  };
};

export default useCombatFilter;
